import { AxiosError } from 'axios';
import api from '../../../services/cle';
import { useRef, useState, useEffect } from 'react';
import { useAppSelector } from '../../../store/hooks';
import { SubmitHandler, useForm } from 'react-hook-form';
import OrganizacaoExclusao from '../../organizacao/exclusao';
import Button from '../../../rxlib/components/buttons/button';
import InputLabel from '../../../rxlib/components/input-label';
import { ApiError, ListagemProps } from '../../../services/tipos';
import { Listagem } from '../../../rxlib/components/layout/listagem';
import ButtonLink from '../../../rxlib/components/buttons/button-link';
import { usuarioTemPermissao } from '../../../rxlib/services/seguranca';
import AcessoBloqueado from '../../../rxlib/components/acesso-bloqueado';
import { RxlibLayout } from '../../../rxlib/components/layout/rxlib-Layout';
import { ModalWarning } from '../../../rxlib/components/modal/modal-warning';
import Breadcrumb, { BreadcrumbItem } from '../../../rxlib/components/breadcrumb';
import { obterQuantidadeParaPular, tratarErroApi } from '../../../rxlib/services/utilitarios';
import { AcoesTabela, ColunaOrdemTabela, ConfiguracoesTabela } from '../../../rxlib/components/table';

interface Filtros {
    nomeSiglaOrganizacao: string;
}

function OrganizacaoListagem(props: ListagemProps) {
    const [exclusao, setExclusao] = useState<boolean>(false);
    const [organizacoes, setOrganizacoes] = useState<[{}]>([{}]);
    const [carregando, setCarregando] = useState<boolean>(false);
    const [idOrganizacao, setIdOrganizacao] = useState<string>('');
    const [token] = useState(useAppSelector(state => state.token));
    const [showWarning, setShowWarning] = useState<boolean>(false);
    const [showExclusao, setShowExclusao] = useState<boolean>(false);
    const [quantidadeTotal, setQuantidadeTotal] = useState<number>(0);
    const [messageWarning, setMessageWarning] = useState<string[]>([]);

    const [filtros, setFiltros] = useState<Filtros>({
        nomeSiglaOrganizacao: '',
    });

    const [colunaOrdem, setColunaOrdem] = useState<ColunaOrdemTabela>({
        campo: '',
        ordem: '',
    });

    const ordenandoRef = useRef<boolean>(false);

    const { register, handleSubmit } = useForm<Filtros>();

    const handleHide = () => setShowWarning(false);

    const handleHideExclusao = () => setShowExclusao(false);

    const breadcrumbs: BreadcrumbItem[] = [
        { texto: 'Home', link: '/home' },
        { texto: 'Organizações e unidades', link: '' },
    ];

    const configuracoesTabela: ConfiguracoesTabela = {
        mensagemPadrao: 'Não foram encontrados resultados para a consulta.',
        colunas: [
            { nome: 'Código', campo: 'Id', tipo: 'string' },
            { nome: 'Nome', campo: 'Nome', tipo: 'string' },
            { nome: 'Chave', campo: 'Chave', tipo: 'string' },
            { nome: 'Sigla', campo: 'Sigla', tipo: 'string' },
            { nome: 'Token', campo: 'Token', tipo: 'string' },
            { nome: 'Data referência estatísticas', campo: 'DataReferenciaEstatisticas', tipo: 'date' },
        ],
    }

    const quantidadeRegistrosPorPagina = 10;
    const quantidadeParaPular = obterQuantidadeParaPular(parseInt(props.match.params.pagina), quantidadeRegistrosPorPagina);

    const paginaAnteriorRef = useRef<number>(quantidadeParaPular);

    const temPermissao = usuarioTemPermissao(token, 'organizacao_visualizar');
    const linkNovo = usuarioTemPermissao(token, 'organizacao_novo') ? '/organizacao/novo' : '';
    const linkEdicao = usuarioTemPermissao(token, 'organizacao_editar') ? '/organizacao/editar' : '';
    const handleExclusao = usuarioTemPermissao(token, 'organizacao_excluir') ? handleExcluir : undefined;

    useEffect(() => {
        if (temPermissao) {
            const mudouPagina = paginaAnteriorRef.current !== quantidadeParaPular;
            paginaAnteriorRef.current = quantidadeParaPular;

            if (!ordenandoRef.current && !mudouPagina) {
                setCarregando(true);
            }

            let request = `/OData/Organizacao?$count=true&$top=${quantidadeRegistrosPorPagina}&$skip=${quantidadeParaPular}`;

            if (filtros.nomeSiglaOrganizacao !== '') {
                request += `&$filter=(contains(toupper(nome),toupper('${filtros.nomeSiglaOrganizacao}')) or contains(toupper(sigla),toupper('${filtros.nomeSiglaOrganizacao}')))`;
            }

            if ((colunaOrdem.campo !== '') && (colunaOrdem.ordem !== '')) {
                request += `&$orderby=${colunaOrdem.campo} ${colunaOrdem.ordem}`;
            }

            api.get(request)
                .then(response => {
                    setTimeout(() => {
                        setQuantidadeTotal(response.data['@odata.count']);
                        setOrganizacoes(response.data.value);
                        setCarregando(false);
                        ordenandoRef.current = false;
                    }, 250);
                }).catch((error: AxiosError<ApiError>) => {
                    setCarregando(false);
                    ordenandoRef.current = false;
                    setMessageWarning(tratarErroApi(error, 'Não foi possível realizar a consulta: '));
                    setShowWarning(true);
                });
        }
    }, [temPermissao, exclusao, filtros, quantidadeParaPular, colunaOrdem]);

    function obterAcoesPersonalizadas(): AcoesTabela[] {
        const acoesPersonalizadas: AcoesTabela[] = [];

        if (usuarioTemPermissao(token, 'licenca_organizacao_visualizar')) {
            acoesPersonalizadas.push({ link: '/organizacao/licenca', nome: 'Licenças', iconeClassName: 'fas fa-book me-3' });
        }

        if (usuarioTemPermissao(token, 'parametros_organizacao_visualizar')) {
            acoesPersonalizadas.push({ link: '/organizacao/parametros', nome: 'Parâmetros', iconeClassName: 'fas fa-cog me-3' });
        }

        return acoesPersonalizadas;
    }

    function handleExcluir(id: string) {
        setIdOrganizacao(id);
        setShowExclusao(true);
    }

    function handleOrdenar(coluna: ColunaOrdemTabela) {
        ordenandoRef.current = true;
        setColunaOrdem(coluna);
    }

    const onSubmit: SubmitHandler<Filtros> = data => {
        setFiltros({
            nomeSiglaOrganizacao: data.nomeSiglaOrganizacao,
        });
    }

    function tratarErro(error: AxiosError<ApiError>, mensagem: string) {
        setCarregando(false);
        setMessageWarning(tratarErroApi(error, mensagem));
        setShowWarning(true);
    }

    return (
        <>
            <RxlibLayout>
                <Breadcrumb
                    itens={breadcrumbs} />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='container-fluid'>
                        <div className='row px-1'>
                            <div className='col-12'>
                                <h6>Listagem de organizações</h6>
                            </div>
                        </div>
                        <div className='row px-1 mt-2'>
                            <div className='col-5'>
                                {
                                    carregando
                                        ? ''
                                        : <InputLabel
                                            type='text'
                                            maxLength={50}
                                            placeholder=''
                                            readOnly={false}
                                            autoFocus={true}
                                            required={false}
                                            name='nomeSiglaOrganizacao'
                                            id='inputNomeSiglaOrganizacao'
                                            className='rxlib-input-label-coluna'
                                            referencia={register({ required: false })}
                                            defaultValue={filtros.nomeSiglaOrganizacao}
                                            label='Pesquisar por nome ou sigla da organização:' />
                                }
                            </div>
                            <div className='col-2 rxlib-celula-botao'>
                                {
                                    carregando
                                        ? ''
                                        : <Button
                                            type='submit'
                                            texto='Pesquisar'
                                            classStyle='btn-rxlib' />
                                }
                            </div>
                            <div className='col-3'></div>
                            <div className='col-2 ps-3 rxlib-celula-botao'>
                                {
                                    carregando
                                        ? ''
                                        : <ButtonLink
                                            texto='Novo'
                                            link={linkNovo}
                                            classStyle='btn-rxlib' />
                                }
                            </div>
                        </div>
                        <div className='row ps-3 mt-2'>
                            <div className='row px-1'>
                                <Listagem
                                    campoId='Id'
                                    className='px-1'
                                    usarOrdenacao={true}
                                    tipoBotaoAcao='button'
                                    listagemDetalhe={false}
                                    carregando={carregando}
                                    linkEdicao={linkEdicao}
                                    onOrdenar={handleOrdenar}
                                    fonteDados={organizacoes}
                                    onExcluir={handleExclusao}
                                    linkListagem='/organizacao'
                                    colunaOrdemTabela={colunaOrdem}
                                    configuracoesTabela={configuracoesTabela}
                                    linkVisualizacao='/organizacao/visualizar'
                                    quantidadeTotalRegistros={quantidadeTotal}
                                    acoesPersonalizadas={obterAcoesPersonalizadas()}
                                    paginaAtual={parseInt(props.match.params.pagina)}
                                    quantidadeRegistrosPorPagina={quantidadeRegistrosPorPagina} />
                            </div>
                        </div>
                    </div>
                </form>
                <ModalWarning
                    show={showWarning}
                    onHide={handleHide}
                    message={messageWarning} />
                <AcessoBloqueado
                    permissao={temPermissao} />
                <OrganizacaoExclusao
                    show={showExclusao}
                    tratarErro={tratarErro}
                    onExcluir={setExclusao}
                    onHide={handleHideExclusao}
                    idOrganizacao={idOrganizacao} />
            </RxlibLayout>
        </>
    );
}

export default OrganizacaoListagem;